// Shared behavior for the Courts of Aetheria series page and its book pages.
class AetheriaBase extends HTMLElement {
  setupMotion() {
    this.models = [...this.querySelectorAll("[data-book-model]")];
    this.motionPreference = matchMedia("(prefers-reduced-motion: reduce)");
    this.motionEnabled = !this.motionPreference.matches;
    this.motionButton = this.querySelector("[data-toggle-motion]");
    this.motionButton?.addEventListener("click", () => {
      this.motionEnabled = !this.motionEnabled;
      this.renderMotion();
    });
    this.motionPreference.addEventListener("change", (event) => {
      this.motionEnabled = !event.matches;
      this.renderMotion();
    });
    const modelStatus = this.querySelector("[data-turn-status]");
    const failedModels = new Set();
    this.models.forEach((viewer) => {
      viewer.addEventListener("load", () => {
        failedModels.delete(viewer);
        if (modelStatus && !failedModels.size) modelStatus.textContent = "";
      });
      viewer.addEventListener("error", () => {
        failedModels.add(viewer);
        if (modelStatus) modelStatus.textContent = viewer.dataset.errorLabel;
      });
      // The slow turn belongs to the visitor once they take hold of a book.
      const release = () => {
        viewer.dataset.userTurned = "true";
        viewer.removeAttribute("auto-rotate");
      };
      viewer.addEventListener("keydown", (event) => {
        if (event.key.startsWith("Arrow") || event.key.startsWith("Page"))
          release();
      });
      viewer.addEventListener("camera-change", (event) => {
        if (event.detail?.source === "user-interaction") release();
      });
    });
    this.renderMotion();
  }
  renderMotion() {
    this.classList.toggle("motion-paused", !this.motionEnabled);
    if (this.motionButton) {
      const label = this.motionEnabled
        ? this.copy.pause_motion
        : this.copy.resume_motion;
      // Icon buttons carry their own play and pause glyphs; only the name changes.
      if (this.motionButton.hasAttribute("data-icon-only"))
        this.motionButton.setAttribute("aria-label", label);
      else this.motionButton.textContent = label;
      this.motionButton.setAttribute(
        "aria-pressed",
        String(!this.motionEnabled),
      );
    }
    this.models.forEach((viewer) =>
      viewer.toggleAttribute(
        "auto-rotate",
        this.motionEnabled && viewer.dataset.userTurned !== "true",
      ),
    );
    this.syncDetails?.();
  }
  // The edition details: one picture per detail, advancing on its own until
  // the visitor picks one. Hover and keyboard focus hold the current picture.
  setupDetails() {
    this.detailTabs = [...this.querySelectorAll("[data-detail-tab]")];
    this.detailSlides = [...this.querySelectorAll("[data-detail-slide]")];
    if (!this.detailTabs.length) return;
    this.detailIndex = 0;
    this.detailRegion = this.querySelector("[data-details]");
    this.detailTabs.forEach((tab, index) =>
      tab.addEventListener("click", () => {
        this.detailChosen = true;
        this.showDetail(index);
        this.syncDetails();
      }),
    );
    ["pointerenter", "focusin"].forEach((type) =>
      this.detailRegion.addEventListener(type, () => {
        this.detailHeld = true;
        this.syncDetails();
      }),
    );
    ["pointerleave", "focusout"].forEach((type) =>
      this.detailRegion.addEventListener(type, () => {
        this.detailHeld = false;
        this.syncDetails();
      }),
    );
    this.detailObserver = new IntersectionObserver(
      (entries) => {
        this.detailVisible = entries[0].isIntersecting;
        this.syncDetails();
      },
      { threshold: 0.35 },
    );
    this.detailObserver.observe(this.detailRegion);
    this.showDetail(0);
  }
  showDetail(index) {
    this.detailIndex = index;
    this.detailTabs.forEach((tab, i) =>
      tab.setAttribute("aria-pressed", String(i === index)),
    );
    this.detailSlides.forEach((slide, i) =>
      slide.classList.toggle("is-active", i === index),
    );
  }
  syncDetails() {
    if (!this.detailTabs?.length) return;
    const run =
      this.motionEnabled &&
      this.detailVisible &&
      !this.detailHeld &&
      !this.detailChosen;
    this.detailRegion.classList.toggle("is-cycling", run);
    if (run && !this.detailTimer) {
      this.detailTimer = setInterval(
        () => this.showDetail((this.detailIndex + 1) % this.detailTabs.length),
        Number(this.detailRegion.dataset.interval) || 4500,
      );
    } else if (!run && this.detailTimer) {
      clearInterval(this.detailTimer);
      this.detailTimer = null;
    }
  }
  // Face buttons that turn one viewer to a named side of the book.
  setupViews(viewer, buttons, radius = "100%") {
    buttons.forEach((button) =>
      button.addEventListener("click", () => {
        viewer.dataset.userTurned = "true";
        viewer.removeAttribute("auto-rotate");
        // Auto-rotation turns the model itself, so square it up first.
        viewer.resetTurntableRotation?.(0);
        // Set the attribute so the chosen side survives a model still loading.
        viewer.setAttribute(
          "camera-orbit",
          `${button.dataset.bookView} ${radius}`,
        );
        buttons.forEach((other) =>
          other.setAttribute("aria-pressed", String(other === button)),
        );
      }),
    );
  }
  // The models carry the illustrated hard cover beneath the jacket panels,
  // so removing the jacket only needs those three materials made invisible.
  setJacket(viewer, buttons, off) {
    viewer.model?.materials.forEach((material) => {
      const surface = material.pbrMetallicRoughness;
      // A matte finish keeps the studio lights from washing out the cover art.
      // Covers with their own foil map already carry the right finish.
      if (
        /_case_/.test(material.name) &&
        !surface.metallicRoughnessTexture?.texture
      )
        surface.setRoughnessFactor(0.7);
      if (!/_jacket_/.test(material.name)) return;
      material.setAlphaMode(off ? "MASK" : "OPAQUE");
      if (off) material.setAlphaCutoff(0.5);
      const color = surface.baseColorFactor;
      surface.setBaseColorFactor([color[0], color[1], color[2], off ? 0 : 1]);
    });
    buttons.forEach((button) =>
      button.setAttribute(
        "aria-pressed",
        String((button.dataset.bookJacket === "off") === off),
      ),
    );
  }
  setupBookInspection() {
    this.inspector = this.querySelector("[data-book-inspector]");
    if (!this.inspector) return;
    const viewer = this.querySelector("[data-inspector-model]");
    const status = this.querySelector("[data-inspector-status]");
    const views = [...this.inspector.querySelectorAll("[data-book-view]")];
    const jacketButtons = [
      ...this.inspector.querySelectorAll("[data-book-jacket]"),
    ];
    let jacketOff = false;
    const applyJacket = () => this.setJacket(viewer, jacketButtons, jacketOff);
    jacketButtons.forEach((button) =>
      button.addEventListener("click", () => {
        jacketOff = button.dataset.bookJacket === "off";
        applyJacket();
      }),
    );
    const reset = () => {
      viewer.cameraOrbit = "122deg 80deg 100%";
      viewer.jumpCameraToGoal?.();
      views.forEach((button) => button.setAttribute("aria-pressed", "false"));
    };
    this.querySelectorAll("[data-inspect-book]").forEach((button) =>
      button.addEventListener("click", () => {
        this.inspectorTrigger = button;
        this.querySelector("[data-inspector-title]").textContent =
          button.dataset.title;
        viewer.alt = button.dataset.title;
        viewer.setAttribute("aria-label", button.dataset.rotateLabel);
        status.textContent = this.inspector.dataset.loadingLabel;
        viewer.poster = button.dataset.poster;
        jacketOff = false;
        this.inspector.showModal();
        reset();
        const source = new URL(button.dataset.model, location.href).href;
        if (viewer.src === source && viewer.loaded) {
          status.textContent = "";
          applyJacket();
        } else viewer.src = source;
      }),
    );
    viewer.addEventListener("load", () => {
      status.textContent = "";
      applyJacket();
    });
    // Materials can arrive just after the load event.
    viewer.addEventListener("scene-graph-ready", applyJacket);
    viewer.addEventListener("error", () => {
      status.textContent = this.inspector.dataset.errorLabel;
    });
    this.querySelector("[data-close-inspector]").addEventListener("click", () =>
      this.inspector.close(),
    );
    this.inspector.addEventListener("close", () =>
      this.inspectorTrigger?.focus({ preventScroll: true }),
    );
    this.inspector.addEventListener("click", (event) => {
      if (event.target !== this.inspector) return;
      const box = this.inspector.getBoundingClientRect();
      if (
        event.clientX < box.left ||
        event.clientX > box.right ||
        event.clientY < box.top ||
        event.clientY > box.bottom
      )
        this.inspector.close();
    });
    views.forEach((button) =>
      button.addEventListener("click", () => {
        viewer.cameraOrbit = `${button.dataset.bookView} 100%`;
        viewer.jumpCameraToGoal?.();
        views.forEach((other) =>
          other.setAttribute("aria-pressed", String(other === button)),
        );
      }),
    );
    this.querySelectorAll("[data-book-zoom]").forEach((button) =>
      button.addEventListener("click", () => {
        const orbit = viewer.getCameraOrbit();
        const radius =
          orbit.radius * (button.dataset.bookZoom === "in" ? 0.85 : 1.15);
        viewer.cameraOrbit = `${orbit.theta}rad ${orbit.phi}rad ${radius}m`;
        viewer.jumpCameraToGoal?.();
      }),
    );
    this.querySelector("[data-book-reset]").addEventListener("click", reset);
  }
  setStatus(text) {
    this.querySelectorAll("[data-cart-status]").forEach((status) => {
      status.textContent = text;
    });
  }
  showCartLink() {
    this.querySelectorAll("[data-error-cart-link]").forEach((link) => {
      link.hidden = false;
    });
  }
  // One guarded cart request. Uncertain outcomes block a duplicate retry.
  async addToCart(items, controls) {
    if (!items.length || this.pending || this.uncertain) return;
    const root = window.Shopify?.routes?.root || "/";
    this.setPending(true);
    controls.forEach((button) => {
      button.textContent = this.copy.adding;
    });
    this.setStatus(this.copy.adding);
    try {
      const response = await fetch(root + "cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({ items }),
      });
      if (!response.ok) {
        this.setPending(false);
        this.render();
        this.setStatus(this.copy.stock_error);
        this.showCartLink();
        return;
      }
      const result = await response.json();
      if (!Array.isArray(result.items) || result.items.length !== items.length)
        throw new Error("unconfirmed");
      this.setPending(false);
      this.render();
      this.setStatus(this.copy.added);
      window.location.assign(root + "cart");
    } catch {
      this.uncertain = true;
      this.setPending(false);
      this.render();
      this.setStatus(this.copy.network_error);
      this.showCartLink();
    }
  }
}

class AetheriaEditions extends AetheriaBase {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.copy = JSON.parse(this.querySelector("[data-copy]").textContent);
    this.inputs = [...this.querySelectorAll("[data-book-choice]")];
    this.presets = [...this.querySelectorAll("[data-preset]")];
    this.toggles = [...this.querySelectorAll("[data-toggle-book]")];
    this.addButton = this.querySelector("[data-add-books]");
    this.floatingButton = this.querySelector("[data-floating-add]");
    this.seriesButtons = [...this.querySelectorAll("[data-add-series]")];
    this.seriesButtons.forEach((button) => {
      button.dataset.label = button.textContent.trim();
      button.addEventListener("click", () => this.addFullSeries());
    });
    this.freeShipping = Number(this.dataset.freeShipping) || 0;
    this.format = new Intl.NumberFormat(
      document.documentElement.lang || "en-US",
      { style: "currency", currency: this.dataset.currency },
    );
    this.querySelector("[data-bundle-form]").addEventListener(
      "submit",
      (event) => {
        event.preventDefault();
        this.addBooks();
      },
    );
    this.floatingButton.addEventListener("click", () => this.addBooks());
    this.addEventListener("change", (event) => {
      if (event.target.matches("[data-book-choice]")) this.render();
    });
    this.presets.forEach((button) =>
      button.addEventListener("click", () => {
        this.selectCount(Number(button.dataset.preset));
        this.render();
      }),
    );
    this.toggles.forEach((button) =>
      button.addEventListener("click", () => {
        const input = this.inputs[Number(button.dataset.toggleBook) - 1];
        if (!input || input.disabled) return;
        input.checked = !input.checked;
        this.render();
      }),
    );
    this.setupDetails();
    this.setupFloatingBar();
    this.setupMotion();
    this.setupBookInspection();
    this.render();
    this.classList.add("is-ready");
  }
  disconnectedCallback() {
    this.inspector?.close();
    this.summaryObserver?.disconnect();
    this.heroObserver?.disconnect();
    this.detailObserver?.disconnect();
    clearInterval(this.detailTimer);
  }
  setupFloatingBar() {
    const floatingBar = this.querySelector("[data-float]");
    this.heroVisible = true;
    this.summaryVisible = false;
    const sync = () =>
      floatingBar.classList.toggle(
        "is-visible",
        !this.heroVisible && !this.summaryVisible,
      );
    this.heroObserver = new IntersectionObserver((entries) => {
      this.heroVisible = entries[0].isIntersecting;
      sync();
    });
    this.heroObserver.observe(this.querySelector(".ae-hero"));
    this.summaryObserver = new IntersectionObserver(
      (entries) => {
        this.summaryVisible = entries[0].isIntersecting;
        sync();
      },
      { threshold: 0.2 },
    );
    this.summaryObserver.observe(this.addButton);
  }
  available() {
    return this.inputs.filter((input) => input.dataset.available === "true");
  }
  selected() {
    return this.inputs.filter((input) => input.checked && !input.disabled);
  }
  // Grow or shrink the selection in reading order while keeping earlier choices.
  selectCount(count) {
    const available = this.available();
    let current = available.filter((input) => input.checked).length;
    for (const input of available) {
      if (current >= count) break;
      if (!input.checked) {
        input.checked = true;
        current += 1;
      }
    }
    for (const input of [...available].reverse()) {
      if (current <= count) break;
      if (input.checked) {
        input.checked = false;
        current -= 1;
      }
    }
  }
  money(cents) {
    return this.format.format(cents / 100);
  }
  render() {
    const selected = this.selected();
    const count = selected.length;
    const availableCount = this.available().length;
    const total = selected.reduce(
      (sum, input) => sum + Number(input.dataset.price),
      0,
    );
    const countLabel =
      count === 0
        ? this.copy.none_selected
        : count === 1
          ? this.copy.one_book
          : this.copy.selected.replace("{count}", count);
    this.querySelector("[data-selected-count]").textContent = countLabel;
    this.querySelector("[data-selected-list]").replaceChildren(
      ...selected.map((input) => {
        const item = document.createElement("li");
        const title = document.createElement("span");
        title.textContent = input.dataset.title;
        const price = document.createElement("span");
        price.className = "ae-line-price";
        price.textContent = this.money(Number(input.dataset.price));
        item.append(title, price);
        return item;
      }),
    );
    this.querySelector("[data-total]").textContent = this.money(total);
    const shipping = this.querySelector("[data-shipping-note]");
    const qualifies = this.freeShipping > 0 && total >= this.freeShipping;
    shipping.textContent = qualifies ? this.copy.shipping_free : "";
    shipping.hidden = !qualifies;
    this.querySelector("[data-live-total]").textContent = count
      ? this.copy.live_total
          .replace("{count}", countLabel)
          .replace("{total}", this.money(total))
      : "";
    this.inputs.forEach((input, index) => {
      input
        .closest("[data-book-card]")
        .classList.toggle("is-selected", input.checked);
      const toggle = this.toggles[index];
      if (!toggle) return;
      toggle.setAttribute("aria-pressed", String(input.checked));
      toggle.querySelector("[data-toggle-label]").textContent = input.checked
        ? this.copy.toggle_added
        : this.copy.toggle_add;
    });
    this.presets.forEach((button) => {
      const size = Number(button.dataset.preset);
      button.setAttribute("aria-pressed", String(count === size));
      button.disabled = this.pending || size > availableCount;
    });
    const blocked = count === 0 || this.pending || this.uncertain;
    const isFullSeries =
      availableCount > 1 && count === availableCount && this.copy.add_all;
    this.addButton.disabled = blocked;
    this.addButton.textContent =
      count === 0
        ? this.copy.choose
        : isFullSeries
          ? this.copy.add_all
          : count === 1
            ? this.copy.add_one
            : this.copy.add_many.replace("{count}", count);
    this.querySelector("[data-empty-selection]").hidden = count > 0;
    this.querySelector("[data-floating-total]").textContent = count
      ? this.money(total)
      : "";
    const shortLabel =
      count > 1 ? this.copy.float_many.replace("{count}", count) : countLabel;
    this.querySelector("[data-floating-count]").textContent = qualifies
      ? `${shortLabel} · ${this.copy.shipping_short}`
      : shortLabel;
    this.floatingButton.disabled = blocked;
    this.floatingButton.textContent = this.addButton.textContent;
    const seriesBlocked =
      availableCount === 0 || this.pending || this.uncertain;
    this.seriesButtons.forEach((button) => {
      button.disabled = seriesBlocked;
      if (!this.pending && button.dataset.label) {
        button.textContent = button.dataset.label;
      }
    });
    if (!this.pending && !this.uncertain) this.setStatus("");
  }
  setPending(value) {
    this.pending = value;
    this.inputs.forEach((input) => {
      input.disabled = value || input.dataset.available !== "true";
    });
    this.toggles.forEach((button, index) => {
      button.disabled =
        value || this.inputs[index]?.dataset.available !== "true";
    });
    this.presets.forEach((button) => {
      button.disabled = value;
    });
    this.addButton.disabled = value;
    this.floatingButton.disabled = value;
    this.seriesButtons.forEach((button) => {
      button.disabled = value;
    });
    this.querySelector("[data-bundle-form]").setAttribute(
      "aria-busy",
      String(value),
    );
  }
  addFullSeries() {
    const available = this.available();
    if (!available.length) return;
    this.selectCount(available.length);
    this.render();
    return this.addToCart(
      available.map((input) => ({ id: Number(input.value), quantity: 1 })),
      [...this.seriesButtons, this.addButton, this.floatingButton],
    );
  }
  addBooks() {
    return this.addToCart(
      this.selected().map((input) => ({ id: Number(input.value), quantity: 1 })),
      [this.addButton, this.floatingButton, ...this.seriesButtons],
    );
  }
  addFullSeries() {
    const available = this.available();
    if (!available.length) return Promise.resolve();
    this.selectCount(available.length);
    this.render();
    return this.addToCart(
      available.map((input) => ({ id: Number(input.value), quantity: 1 })),
      [...this.seriesButtons, this.addButton, this.floatingButton],
    );
  }
}

class AetheriaBook extends AetheriaBase {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;
    this.copy = JSON.parse(this.querySelector("[data-copy]").textContent);
    this.addButtons = [...this.querySelectorAll("[data-add-book]")];
    this.addButtons.forEach((button) => {
      button.dataset.label = button.textContent;
      button.addEventListener("click", () =>
        this.addToCart(
          [{ id: Number(button.dataset.variant), quantity: 1 }],
          this.addButtons,
        ),
      );
    });
    const viewer = this.querySelector("[data-book-model]");
    this.setupViews(viewer, [
      ...this.querySelectorAll("[data-page-views] button"),
    ]);
    this.setupDetails();
    this.setupMotion();
    this.setupBookInspection();
    this.classList.add("is-ready");
  }
  disconnectedCallback() {
    this.inspector?.close();
    this.detailObserver?.disconnect();
    clearInterval(this.detailTimer);
  }
  render() {
    this.addButtons.forEach((button) => {
      button.textContent = button.dataset.label;
      button.disabled =
        this.pending ||
        this.uncertain ||
        button.dataset.available !== "true";
    });
    if (!this.pending && !this.uncertain) this.setStatus("");
  }
  setPending(value) {
    this.pending = value;
    this.addButtons.forEach((button) => {
      button.disabled = value;
      button.setAttribute("aria-busy", String(value));
    });
  }
}

if (!customElements.get("aetheria-editions"))
  customElements.define("aetheria-editions", AetheriaEditions);
if (!customElements.get("aetheria-book"))
  customElements.define("aetheria-book", AetheriaBook);
